import { lazy, Suspense, useEffect, type ReactNode } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
  useLocation,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { Toaster } from "sonner";

import { useAuthStore } from "@/store";
import { setAuthStateGetter } from "@/api/client";
import AppLayout from "@/components/layout/AppLayout";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { getRewardfulId } from "@/utils/rewardful";

setAuthStateGetter(() => {
  const s = useAuthStore.getState();
  return {
    token: s.token,
    refreshToken: s.refreshToken,
    setToken: s.setToken,
    logout: s.logout,
  };
});

const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Jobs = lazy(() => import("@/pages/Jobs"));
const JobProfile = lazy(() => import("@/pages/JobProfile"));
const JobQuestions = lazy(() => import("@/pages/JobQuestions"));
const Analytics = lazy(() => import("@/pages/Analytics"));
const PlatformSelector = lazy(() => import("@/pages/PlatformSelector"));
const Subscription = lazy(() => import("@/pages/Subscription"));
const Profile = lazy(() => import("@/pages/Profile"));
const Settings = lazy(() => import("@/pages/Settings"));
const Login = lazy(() => import("@/pages/Login"));
const Register = lazy(() => import("@/pages/Register"));
const VerifyOtp = lazy(() => import("@/pages/VerifyOtp"));
const Onboarding = lazy(() => import("@/pages/Onboarding"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const ResumeBuilder = lazy(() => import("@/pages/ResumeBuilder"));
const Landing = lazy(() => import("@/pages/Landing"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const TermsAndConditions = lazy(() => import("@/pages/TermsAndConditions"));
const RefundPolicy = lazy(() => import("@/pages/RefundPolicy"));
const AccessibilityStatement = lazy(() => import("@/pages/AccessibilityStatement"));
const Help = lazy(() => import("@/pages/Help"));
const Affiliate = lazy(() => import("@/pages/Affiliate"));
const Blog = lazy(() => import("@/pages/Blog"));
const Contact = lazy(() => import("@/pages/Contact"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: false, staleTime: 30_000 },
    mutations: { retry: 0 },
  },
});

function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="h-8 w-8 rounded-full border-2 border-brand-200 border-t-brand-600 animate-spin" />
    </div>
  );
}

function RequireAuth() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const location = useLocation();
  if (!isAuthenticated)
    return <Navigate to="/login" state={{ from: location }} replace />;
  return <Outlet />;
}

function RequireGuest() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  if (isAuthenticated) return <Navigate to="/dashboard" replace />;
  return <Outlet />;
}

/** Renders different content based on auth state */
function AuthGate({
  authenticated,
  guest,
}: {
  authenticated: ReactNode;
  guest: ReactNode;
}) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  return <>{isAuthenticated ? authenticated : guest}</>;
}

export default function App() {
  useEffect(() => {
    getRewardfulId();
  }, []);

  // Initialize extension storage on app load
  useEffect(() => {
    import("@/lib/extensionStorage").then(({ initializeExtensionStorage }) => {
      initializeExtensionStorage();
    });
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route element={<RequireGuest />}>
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/verify-otp" element={<VerifyOtp />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
            </Route>

            {/* Public landing page — redirect to dashboard if already logged in */}
            <Route
              index
              element={
                <AuthGate
                  authenticated={<Navigate to="/dashboard" replace />}
                  guest={<Landing />}
                />
              }
            />

            <Route element={<RequireAuth />}>
              <Route path="/onboarding" element={<Onboarding />} />
              {/* Resume builder is full-screen, no sidebar */}
              <Route path="/resume-builder" element={<ResumeBuilder />} />

              <Route element={<AppLayout />}>
                <Route
                  path="/dashboard"
                  element={
                    <ErrorBoundary>
                      <Dashboard />
                    </ErrorBoundary>
                  }
                />
                <Route
                  path="/jobs"
                  element={
                    <ErrorBoundary>
                      <Jobs />
                    </ErrorBoundary>
                  }
                />
                <Route
                  path="/job-profile"
                  element={
                    <ErrorBoundary>
                      <JobProfile />
                    </ErrorBoundary>
                  }
                />
                <Route
                  path="/job-questions"
                  element={
                    <ErrorBoundary>
                      <JobQuestions />
                    </ErrorBoundary>
                  }
                />
                <Route
                  path="/analytics"
                  element={
                    <ErrorBoundary>
                      <Analytics />
                    </ErrorBoundary>
                  }
                />
                <Route
                  path="/browse-jobs"
                  element={
                    <ErrorBoundary>
                      <PlatformSelector />
                    </ErrorBoundary>
                  }
                />
                <Route
                  path="/subscription"
                  element={
                    <ErrorBoundary>
                      <Subscription />
                    </ErrorBoundary>
                  }
                />
                <Route
                  path="/profile"
                  element={
                    <ErrorBoundary>
                      <Profile />
                    </ErrorBoundary>
                  }
                />
                <Route
                  path="/settings"
                  element={
                    <ErrorBoundary>
                      <Settings />
                    </ErrorBoundary>
                  }
                />
                <Route path="/manage-job-profile" element={<Navigate to="/job-profile" replace />} />
              </Route>
            </Route>

            {/* Public legal / info pages */}
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
            <Route path="/refund-policy" element={<RefundPolicy />} />
            <Route path="/accessibility-statement" element={<AccessibilityStatement />} />
            <Route path="/help" element={<Help />} />
            <Route path="/affiliate" element={<Affiliate />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/contact" element={<Contact />} />

            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>

      <Toaster
        position="top-right"
        richColors
        closeButton
        toastOptions={{
          duration: 4000,
          style: { fontFamily: '"DM Sans", system-ui, sans-serif' },
        }}
      />

      {import.meta.env.DEV && (
        <ReactQueryDevtools initialIsOpen={false} position="bottom" />
      )}
    </QueryClientProvider>
  );
}
